/**
 * Whether the signed-in account may view a module, from the permissions the
 * server resolved on /auth/me (app/core/rbac.py) — the same rule the sidebar
 * uses (Layout.tsx). A screen inside an in-scope page (the dossier) uses it to
 * skip a read the API would refuse, instead of showing that refusal as an
 * outage: the Step-1 analyst role has no Closing module (25 Sep 2026).
 */
export type PermissionHolder = {
  is_super_admin?: boolean | null
  permissions?: Record<string, string[]>
  /**
   * A session whose grants are not known yet: an Entra session before
   * /auth/me answers, or when it cannot (useSessionPermissions). It is
   * granted nothing, unlike an absent profile — a screen waits for the answer
   * rather than offering a viewer what the API would refuse.
   */
  pending?: boolean
} | null | undefined

function allowed(user: PermissionHolder, moduleId: string, action: string): boolean {
  if (!user) return true
  if (user.pending) return false
  if (user.is_super_admin) return true
  const permissions = user.permissions
  if (!permissions) return true
  const actions = permissions[moduleId]
  return actions ? actions.includes(action) : true
}

export function canViewModule(user: PermissionHolder, moduleId: string): boolean {
  return allowed(user, moduleId, 'view')
}

/**
 * Whether the account may CHANGE something in a module — the other half of the
 * same rule. A Viewer reads a screen that offers decisions and meets a refusal
 * for each one it tries; a screen asks this first and draws the rows read-only.
 * Presentation only: the API stays authoritative either way, and an absent
 * profile (an older cached one) draws everything rather than blank the screen.
 */
export function canEditModule(user: PermissionHolder, moduleId: string): boolean {
  return allowed(user, moduleId, 'edit')
}

/** Whether the account may export in a module (run a report and take the file). */
export function canExportModule(user: PermissionHolder, moduleId: string): boolean {
  return allowed(user, moduleId, 'export')
}

/** Whether the account may approve in a module (activate a schedule). */
export function canApproveModule(user: PermissionHolder, moduleId: string): boolean {
  return allowed(user, moduleId, 'approve')
}
